import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { apiGet } from "../../processes/helpers/api";
import { MultiSelectDropdown, SelectFilter } from "./TeacherMocksShared";
import QuestionPreview from "./QuestionPreview";
import {
  advancedBooleanFilters,
  cognitiveOptions,
  difficultyOptions,
  formatOptions,
  questionDefaultFilters,
  recentFilterOptions,
} from "./teacherMocksConstants";
import {
  managerQuery,
  questionFiltersToQuery,
} from "./teacherMocksHelpers";

export default function QuestionSelectorModal({ courseId, excludeQuestionIds, onAddQuestions, onClose }) {
  const [filters, setFilters] = useState(questionDefaultFilters(courseId));
  const [appliedFilters, setAppliedFilters] = useState(questionDefaultFilters(courseId));
  const [levels, setLevels] = useState([]);
  const [levelSearch, setLevelSearch] = useState("");
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [isLevelDropdownOpen, setIsLevelDropdownOpen] = useState(false);
  const levelInputRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pendingIds, setPendingIds] = useState(new Set());

  useEffect(() => {
    setFilters(questionDefaultFilters(courseId));
    setAppliedFilters(questionDefaultFilters(courseId));
    setQuestions([]);
    setPendingIds(new Set());
    setSelectedLevels([]);
    setLevelSearch("");
    setLevels([]);
  }, [courseId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAppliedFilters(filters);
    }, 500);

    return () => clearTimeout(timeout);
  }, [filters]);

  useEffect(() => {
    let isMounted = true;

    async function loadQuestions() {
      try {
        setIsLoading(true);
        const response = await apiGet(`/api/v2/manager/questions?${questionFiltersToQuery(appliedFilters, 1)}`);
        if (!isMounted) return;
        setQuestions(response.body?.data || []);
        setPagination(response.body?.pagination || null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    loadQuestions();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters]);

  useEffect(() => {
    if (!courseId) {
      setLevels([]);
      return undefined;
    }

    let isMounted = true;
    const normalizedSearch = levelSearch.trim();
    const timeout = setTimeout(async () => {
      try {
        setIsLoadingLevels(true);
        const query = managerQuery({
          "q[course_id_eq]": courseId,
          "q[name_cont]": normalizedSearch,
          "q[with_questions]": true,
          "q[s]": "name asc",
          paginated: true,
          page: 1,
          per_page: 20,
        });
        const response = await apiGet(`/api/v2/manager/levels?${query}`);
        if (isMounted) setLevels(response.body?.data || []);
      } finally {
        if (isMounted) setIsLoadingLevels(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [courseId, levelSearch]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const toggleLevel = (levelId) => {
    const parsedId = Number(levelId);
    const level = levels.find((item) => Number(item.id) === parsedId);

    setFilters((current) => {
      const selected = current.fromLevelsExpanded.includes(parsedId);
      return {
        ...current,
        fromLevelsExpanded: selected
          ? current.fromLevelsExpanded.filter((id) => id !== parsedId)
          : [...current.fromLevelsExpanded, parsedId],
      };
    });

    setSelectedLevels((current) => (
      current.some((item) => Number(item.id) === parsedId)
        ? current.filter((item) => Number(item.id) !== parsedId)
        : [...current, level || { id: parsedId, name: `#${parsedId}` }]
    ));
  };

  const clearFilters = () => {
    setFilters(questionDefaultFilters(courseId));
    setLevelSearch("");
    setLevels([]);
    setSelectedLevels([]);
    setIsLevelDropdownOpen(false);
  };

  const isExcluded = (questionId) => excludeQuestionIds.some((id) => Number(id) === Number(questionId));

  const togglePending = useCallback((questionId) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      const numId = Number(questionId);
      if (next.has(numId)) {
        next.delete(numId);
      } else {
        next.add(numId);
      }
      return next;
    });
  }, []);

  const selectableQuestionIds = useMemo(
    () => questions
      .filter((question) => !excludeQuestionIds.some((id) => Number(id) === Number(question.id)))
      .map((question) => Number(question.id)),
    [questions, excludeQuestionIds]
  );
  const allSelectableQuestionsSelected = selectableQuestionIds.length > 0
    && selectableQuestionIds.every((questionId) => pendingIds.has(questionId));

  const toggleAllPending = useCallback(() => {
    setPendingIds((prev) => {
      const next = new Set(prev);

      if (selectableQuestionIds.every((questionId) => next.has(questionId))) {
        selectableQuestionIds.forEach((questionId) => next.delete(questionId));
      } else {
        selectableQuestionIds.forEach((questionId) => next.add(questionId));
      }

      return next;
    });
  }, [selectableQuestionIds]);

  const handleConfirm = useCallback(() => {
    const selected = questions.filter((q) => pendingIds.has(Number(q.id)));
    onAddQuestions(selected);
    setPendingIds(new Set());
    onClose();
  }, [questions, pendingIds, onAddQuestions, onClose]);

  const loadMore = async () => {
    if (!pagination?.hasNext || isLoading) return;
    try {
      setIsLoading(true);
      const nextPage = (pagination.page || 1) + 1;
      const response = await apiGet(`/api/v2/manager/questions?${questionFiltersToQuery(appliedFilters, nextPage)}`);
      const newQuestions = response.body?.data || [];
      setQuestions((current) => {
        const ids = new Set(current.map((question) => Number(question.id)));
        return [...current, ...newQuestions.filter((question) => !ids.has(Number(question.id)))];
      });
      setPagination(response.body?.pagination || null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="teacher-mocks-selector-overlay" onMouseDown={onClose}>
      <div className="teacher-mocks-selector" onMouseDown={(event) => event.stopPropagation()}>
        <div className="teacher-mocks-selector-header">
          <div>
            <h3>Selecionar Questões</h3>
            <p>Selecione questões para adicionar ao simulado</p>
          </div>
          <button type="button" className="teacher-mocks-icon-button" onClick={onClose} aria-label="Fechar seleção">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="teacher-mocks-selector-body">
          <div className="teacher-mocks-selector-filters">
            <div className="teacher-mocks-selector-main-filters">
              <div className="teacher-mocks-manager-field teacher-mocks-selector-id-field">
                <label>ID</label>
                <input
                  type="number"
                  value={filters.idEq}
                  onChange={(event) => updateFilter("idEq", event.target.value)}
                  placeholder="ID..."
                />
              </div>
              <div className="teacher-mocks-manager-field teacher-mocks-selector-context-field">
                <label>Contexto</label>
                <input
                  value={filters.contextCont}
                  onChange={(event) => updateFilter("contextCont", event.target.value)}
                  placeholder="Buscar por contexto..."
                />
              </div>
              <div className="teacher-mocks-manager-field teacher-mocks-selector-levels-field">
                <label>Módulos ou sub-módulos</label>
                <div
                  className="teacher-mocks-level-combobox"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      setIsLevelDropdownOpen(false);
                    }
                  }}
                >
                  <div
                    className="teacher-mocks-level-control"
                    onMouseDown={(event) => {
                      if (!event.target.closest("button")) {
                        levelInputRef.current?.focus();
                        setIsLevelDropdownOpen(true);
                      }
                    }}
                  >
                    {selectedLevels.map((level) => (
                      <button
                        key={level.id}
                        type="button"
                        className="teacher-mocks-level-chip"
                        onClick={() => toggleLevel(level.id)}
                      >
                        <span>{level.name}</span>
                        <X size={12} aria-hidden="true" />
                      </button>
                    ))}
                    <input
                      ref={levelInputRef}
                      value={levelSearch}
                      onFocus={() => setIsLevelDropdownOpen(true)}
                      onChange={(event) => {
                        setLevelSearch(event.target.value);
                        setIsLevelDropdownOpen(true);
                      }}
                      placeholder={selectedLevels.length ? "Buscar outro módulo..." : "Buscar módulos..."}
                    />
                    <ChevronDown size={14} aria-hidden="true" />
                  </div>
                  {isLevelDropdownOpen && (
                    <div className="teacher-mocks-level-dropdown">
                      {isLoadingLevels && <div className="teacher-mocks-level-option muted">Carregando...</div>}
                      {!isLoadingLevels && levels.length === 0 && (
                        <div className="teacher-mocks-level-option muted">Nenhum módulo encontrado</div>
                      )}
                      {levels.map((level) => {
                        const selected = filters.fromLevelsExpanded.includes(Number(level.id));
                        return (
                          <button
                            key={level.id}
                            type="button"
                            className={`teacher-mocks-level-option${selected ? " selected" : ""}`}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => toggleLevel(level.id)}
                          >
                            <span>{level.name}</span>
                            {selected && <Check size={14} aria-hidden="true" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="teacher-mocks-advanced">
              <button type="button" onClick={() => setShowAdvanced((current) => !current)}>
                Filtros avançados
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              {showAdvanced && (
                <div className="teacher-mocks-advanced-content">
                  <div className="teacher-mocks-advanced-grid">
                    <MultiSelectDropdown
                      label="Dificuldade"
                      options={difficultyOptions}
                      value={filters.difficultyLevelIn}
                      onChange={(value) => updateFilter("difficultyLevelIn", value)}
                      placeholder="Selecione a dificuldade"
                    />
                    <MultiSelectDropdown
                      label="Nível Cognitivo"
                      options={cognitiveOptions}
                      value={filters.cognitiveLevelIn}
                      onChange={(value) => updateFilter("cognitiveLevelIn", value)}
                      placeholder="Selecione o nível cognitivo"
                    />
                    <SelectFilter
                      label="Formato"
                      value={filters.formatEq}
                      onChange={(value) => updateFilter("formatEq", value)}
                      options={formatOptions}
                    />
                    <div className="teacher-mocks-manager-field">
                      <label>Professor</label>
                      <input type="number" value={filters.teacherIdEq} onChange={(event) => updateFilter("teacherIdEq", event.target.value)} placeholder="ID do professor" />
                    </div>
                    <div className="teacher-mocks-manager-field">
                      <label>Revisor</label>
                      <input type="number" value={filters.reviewerIdEq} onChange={(event) => updateFilter("reviewerIdEq", event.target.value)} placeholder="ID do revisor" />
                    </div>
                    <div className="teacher-mocks-manager-field">
                      <label>Tags</label>
                      <input value={filters.tagsNameIn} onChange={(event) => updateFilter("tagsNameIn", event.target.value)} placeholder="Tags separadas por vírgula" />
                    </div>
                    <div className="teacher-mocks-manager-field">
                      <label>Notas do Professor</label>
                      <input value={filters.teacherNotesCont} onChange={(event) => updateFilter("teacherNotesCont", event.target.value)} placeholder="Buscar nas notas..." />
                    </div>
                    <SelectFilter
                      label="Apenas Recentes"
                      value={filters.recentFilter}
                      onChange={(value) => updateFilter("recentFilter", value)}
                      options={recentFilterOptions}
                      placeholder="Selecione o período"
                    />
                  </div>

                  <div className="teacher-mocks-advanced-boolean-grid">
                    {advancedBooleanFilters.map((filter) => (
                      <SelectFilter
                        key={filter.field}
                        label={filter.label}
                        value={filters[filter.field]}
                        onChange={(value) => updateFilter(filter.field, value)}
                        options={filter.options}
                        placeholder="Selecione uma opção"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="teacher-mocks-selector-summary">
              <span>
                {pagination?.totalItems != null
                  ? `${pagination.totalItems} ${pagination.totalItems === 1 ? "questão encontrada" : "questões encontradas"}`
                  : isLoading ? "Buscando..." : ""}
              </span>
              <div className="teacher-mocks-selector-summary-actions">
                <label className="teacher-mocks-select-all">
                  <input
                    type="checkbox"
                    checked={allSelectableQuestionsSelected}
                    disabled={selectableQuestionIds.length === 0}
                    onChange={toggleAllPending}
                  />
                  <span>Selecionar todas</span>
                </label>
                <button type="button" onClick={clearFilters}>Limpar filtros</button>
              </div>
            </div>
          </div>

          <div className="teacher-mocks-selector-list">
            {questions.length === 0 && !isLoading && (
              <div className="teacher-mocks-selector-empty">
                <strong>Nenhuma questão encontrada</strong>
                <span>Tente remover alguns filtros, buscar por outro contexto ou selecionar um módulo diferente.</span>
                <button type="button" onClick={clearFilters}>Limpar todos os filtros</button>
              </div>
            )}

            {questions.map((question) => {
              const excluded = isExcluded(question.id);
              const pending = pendingIds.has(Number(question.id));
              return (
                <QuestionPreview
                  key={question.id}
                  question={question}
                  disabled={excluded}
                  selected={pending}
                  onToggle={excluded ? undefined : () => togglePending(question.id)}
                  action={excluded ? (
                    <span className="teacher-mocks-question-added-label">Já adicionada</span>
                  ) : null}
                />
              );
            })}

            {isLoading && <div className="teacher-mocks-selector-loading">Carregando...</div>}
            {pagination?.hasNext && !isLoading && (
              <button type="button" className="teacher-mocks-load-more" onClick={loadMore}>
                Carregar mais
              </button>
            )}
          </div>
        </div>

        <div className="teacher-mocks-selector-footer">
          <span className="teacher-mocks-selector-count">
            {pendingIds.size === 0
              ? "Nenhuma questão selecionada"
              : `${pendingIds.size} ${pendingIds.size === 1 ? "questão selecionada" : "questões selecionadas"}`}
          </span>
          <div className="teacher-mocks-selector-footer-actions">
            <button type="button" className="teacher-mocks-secondary-button" onClick={onClose}>
              Fechar
            </button>
            <button
              type="button"
              className="teacher-mocks-primary-button"
              disabled={pendingIds.size === 0}
              onClick={handleConfirm}
            >
              <Plus size={14} aria-hidden="true" />
              {pendingIds.size === 0
                ? "Adicionar questões"
                : `Adicionar ${pendingIds.size} ${pendingIds.size === 1 ? "questão" : "questões"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
